import { useState } from 'react';
import { T, Var } from 'gt-react';
import type {
  ConfirmCartsInput,
  FreightOptionRow,
  SubmissionDefaults,
} from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  useDraftFreight,
  useFreightOptions,
  useSuggestOrderFreight,
} from 'src/api/rpc/hooks/procurement/useFreightOptions';
import {
  useDraftSubmission,
  useSubmissionDefaults,
} from 'src/api/rpc/hooks/procurement/usePurchaseOrderSubmission';
import { FreightOptionFields } from 'src/components/procurement/draft-purchase-order/DraftFreightEditor';
import { DraftSubmissionFields } from 'src/components/procurement/draft-purchase-order/DraftSubmissionEditor';
import {
  pickedOptionStart,
  releaseFreightChoice,
} from 'src/components/procurement/draft-purchase-order/draftFreightFormat';
import {
  releaseBaseline,
  type Preselected,
  type ReleaseBaseline,
} from '../utils/releaseBaseline';
import {
  changedSubmissionFields,
  formFromSubmission,
  type DraftSubmissionForm,
} from 'src/components/procurement/draft-purchase-order/draftSubmissionForm';

/** The freight and sending a buyer picked for the cart being released. */
export type CartReleaseChoice = Pick<
  ConfirmCartsInput['carts'][number],
  'freight' | 'sending'
>;

/**
 * Freight and sending in the release dialog (GEN-3277), as on GAINS's order
 * screen: preselected with the branch's suggested freight and the vendor's
 * sending defaults (or, when adding to an open PO, that PO's own), and
 * optional. `onChange` reports only what the buyer changed; releasing without
 * a change behaves as before.
 */
export function ReleaseFreightAndSending({
  onChange,
  ...scope
}: ReleaseScope & { onChange: (choice: CartReleaseChoice) => void }) {
  const baseline = useReleaseBaseline(scope);
  if (baseline.status === 'failed')
    return (
      <p className="text-xs text-amber-700">
        <T context="release dialog: freight and sending could not load">
          Freight and sending could not load. Releasing keeps the suggested
          freight and the vendor's sending defaults.
        </T>
      </p>
    );
  if (baseline.status === 'loading')
    return (
      <p className="text-xs text-gray-500">
        <T context="release dialog: freight and sending loading">
          Loading freight and sending…
        </T>
      </p>
    );
  return <ReleaseChoices {...baseline} onChange={onChange} />;
}

interface ReleaseScope {
  supplierId: string;
  shipToWarehouseId: string;
  orderValue: number;
  lines: { productV3Key: string | null; quantity: number }[];
  appendToDraftPurchaseOrderId: string | null;
}

/**
 * What the dialog starts from: the branch's freight options, the suggestion
 * for these lines, and the open PO's own freight and sending when adding to
 * it, else the vendor's sending defaults.
 */
function useReleaseBaseline(scope: ReleaseScope): ReleaseBaseline {
  const { supplierId, shipToWarehouseId: warehouseId } = scope;
  const draftId = scope.appendToDraftPurchaseOrderId;
  const options = useFreightOptions({ warehouseId });
  const suggestion = useSuggestOrderFreight({
    supplierId,
    warehouseId,
    orderValue: scope.orderValue,
    lines: scope.lines,
    ...(draftId !== null && { draftPurchaseOrderId: draftId }),
  });
  const draftFreight = useDraftFreight(draftId);
  const draftSubmission = useDraftSubmission(draftId);
  const defaults = useSubmissionDefaults(
    draftId === null ? { supplierId, warehouseId } : null,
  );
  const appending = draftId !== null;
  return releaseBaseline({
    supplierId,
    failed: [options, suggestion, draftFreight, draftSubmission, defaults].some(
      (query) => query.isError,
    ),
    options: options.data?.options,
    suggestion: suggestion.data,
    sending: appending ? draftSubmission.data : defaults.data,
    current: appending
      ? draftFreight.data && {
          optionId: draftFreight.data.freightOptionId,
          carrierAccount: draftFreight.data.carrierAccount,
          instructions: draftFreight.data.instructions,
        }
      : null,
  });
}

function ReleaseChoices({
  options,
  suggestedOptionId,
  preselected,
  sending,
  onChange,
}: {
  options: FreightOptionRow[];
  suggestedOptionId: string | null;
  preselected: Preselected;
  sending: SubmissionDefaults;
  onChange: (choice: CartReleaseChoice) => void;
}) {
  const [optionId, setOptionId] = useState(preselected.optionId ?? '');
  const [carrierAccount, setCarrierAccount] = useState(
    preselected.carrierAccount ?? '',
  );
  // The picks start from the baseline and then belong to the buyer: a refetch
  // of the baseline must not overwrite them, and the parent remounts this per
  // release target.
  // react-doctor-disable-next-line react-doctor/no-derived-useState -- initial value of an edited form, by design.
  const [instructions, setInstructions] = useState(preselected.instructions);
  const [form, setForm] = useState(() => formFromSubmission(sending));
  const [editingSending, setEditingSending] = useState(false);

  function report(
    changed: Partial<{
      optionId: string;
      carrierAccount: string;
      instructions: string[];
      form: DraftSubmissionForm;
    }>,
  ) {
    const next = { optionId, carrierAccount, instructions, form, ...changed };
    const changedSending = changedSubmissionFields(sending, next.form);
    onChange({
      freight: releaseFreightChoice({
        option: options.find((option) => option.id === next.optionId),
        carrierAccount: next.carrierAccount,
        instructions: next.instructions,
        preselected,
      }),
      sending:
        Object.keys(changedSending).length > 0 ? changedSending : undefined,
    });
  }

  function pick(id: string) {
    const start = pickedOptionStart(options, id);
    setOptionId(id);
    setCarrierAccount(start.carrierAccount);
    setInstructions(start.instructions);
    report({ optionId: id, ...start });
  }

  return (
    <div className="space-y-3 rounded border border-gray-100 p-2">
      <section className="grid gap-2">
        <h3 className="text-sm font-semibold">
          <T context="release dialog section heading">Freight</T>
        </h3>
        {options.length === 0 ? (
          <p className="text-xs text-gray-500">
            <T context="release dialog: the branch has no confirmed freight options">
              This branch has no confirmed freight options. The vendor's default
              applies.
            </T>
          </p>
        ) : (
          <FreightOptionFields
            idPrefix="release-freight"
            options={options}
            suggestedOptionId={suggestedOptionId}
            optionId={optionId}
            carrierAccount={carrierAccount}
            instructions={instructions}
            onPick={pick}
            onCarrierAccount={(account) => {
              setCarrierAccount(account);
              report({ carrierAccount: account });
            }}
            onInstructions={(lines) => {
              setInstructions(lines);
              report({ instructions: lines });
            }}
          />
        )}
      </section>
      <section className="grid gap-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">
            <T context="release dialog section heading: how the PO is sent to the vendor">
              Sending
            </T>
          </h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEditingSending(!editingSending)}
          >
            {editingSending ? (
              <T context="release dialog: hide the sending fields">Hide</T>
            ) : (
              <T context="release dialog: show the sending fields to change them">
                Change
              </T>
            )}
          </Button>
        </div>
        {editingSending ? (
          <DraftSubmissionFields
            idPrefix="release-sending"
            form={form}
            setup={sending}
            onChange={(next) => {
              setForm(next);
              report({ form: next });
            }}
          />
        ) : (
          <SendingSummary form={form} />
        )}
      </section>
    </div>
  );
}

function SendingSummary({ form }: { form: DraftSubmissionForm }) {
  if (form.sendByEdi)
    return (
      <p className="text-xs text-gray-700">
        <T context="release dialog: the PO goes to the vendor by EDI">
          Sent by EDI
        </T>
      </p>
    );
  if (!form.emailTo.trim())
    return (
      <p className="text-xs text-amber-700">
        <T context="release dialog: the PO has no email recipient">
          No email recipient yet.
        </T>
      </p>
    );
  return (
    <p className="text-xs text-gray-700">
      <T context="release dialog: the PO goes to the vendor by email">
        Emailed to <Var>{form.emailTo}</Var>
      </T>
    </p>
  );
}
